import React, { useState, useEffect } from 'react';
import { whatsappAPI } from '../../services/api';
import { 
  BarChart3, 
  Users, 
  Send, 
  Settings, 
  Plus, 
  Edit, 
  Trash2, 
  Play,
  Pause,
  Calendar,
  Target,
  TrendingUp,
  DollarSign,
  Eye,
  MousePointer,
  CheckCircle,
  AlertCircle,
  Clock,
  Star,
  Gift,
  Heart,
  Zap
} from 'lucide-react';

const WhatsAppMarketing = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [segments, setSegments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [abTests, setAbTests] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showAddSetting, setShowAddSetting] = useState(false);
  const [newSetting, setNewSetting] = useState({
    key: '',
    value: '',
    type: 'boolean'
  });

  // Form states
  const [segmentForm, setSegmentForm] = useState({
    segment_name: '',
    segment_type: 'custom',
    criteria: {},
    description: ''
  });

  const [campaignForm, setCampaignForm] = useState({
    campaign_name: '',
    campaign_type: 'promotional',
    segment_id: '',
    message_content: '',
    scheduled_at: '',
    is_scheduled: false
  });

  const [abTestForm, setAbTestForm] = useState({
    test_name: '',
    campaign_id: '',
    variant_a_content: '',
    variant_b_content: '',
    test_size: 1000
  });

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'dashboard':
          const dashboardData = await whatsappAPI.getAnalytics();
          if (dashboardData.data.success) {
            setDashboard(dashboardData.data.data);
          }
          break;
        case 'segments':
          // For segments, we'll use a different approach or create a separate endpoint
          setSegments([]);
          break;
        case 'campaigns':
          const campaignsData = await whatsappAPI.getMarketingCampaigns();
          if (campaignsData.data.success) {
            setCampaigns(campaignsData.data.data);
          }
          break;
        case 'ab-tests':
          // For A/B tests, we'll use a different approach or create a separate endpoint
          setAbTests([]);
          break;
        case 'settings':
          try {
            console.log('🔍 Loading marketing settings...');
            const settingsResponse = await whatsappAPI.getMarketingSettings();
            console.log('🔍 Marketing settings response:', settingsResponse);
            
            if (settingsResponse.data.success) {
              console.log('🔍 Setting marketing settings state:', settingsResponse.data.data);
              setSettings(settingsResponse.data.data);
            } else {
              console.error('Failed to load marketing settings:', settingsResponse.data);
              setSettings({});
            }
          } catch (error) {
            console.error('Error loading marketing settings:', error);
            setSettings({});
          }
          break;
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createSegment = async (e) => {
    e.preventDefault();
    try {
      const response = await whatsappAPI.createSegment(segmentForm);
      if (response.data.success) {
        setMessage('Customer segment created successfully');
        setSegmentForm({
          segment_name: '',
          segment_type: 'custom',
          criteria: {},
          description: ''
        });
        loadData();
      } else {
        setMessage('Error creating segment');
      }
    } catch (error) {
      setMessage('Error creating segment');
      console.error('Error creating segment:', error);
    }
  };

  const createCampaign = async (e) => {
    e.preventDefault();
    try {
      const response = await whatsappAPI.createMarketingCampaign(campaignForm);
      if (response.data.success) {
        setMessage('Campaign created successfully');
        setCampaignForm({
          campaign_name: '',
          campaign_type: 'promotional',
          segment_id: '',
          message_content: '',
          scheduled_at: '',
          is_scheduled: false
        });
        loadData();
      }
    } catch (error) {
      setMessage('Error creating campaign');
      console.error('Error creating campaign:', error);
    }
  };

  const executeCampaign = async (campaignId) => {
    try {
      const response = await whatsappAPI.executeCampaign(campaignId);
      if (response.data.success) {
        setMessage('Campaign executed successfully');
        loadData();
      } else {
        setMessage('Error executing campaign');
      }
    } catch (error) {
      setMessage('Error executing campaign');
      console.error('Error executing campaign:', error);
    }
  };

  const createABTest = async (e) => {
    e.preventDefault();
    try {
      const response = await whatsappAPI.createABTest(abTestForm);
      if (response.data.success) {
        setMessage('A/B test created successfully');
        setAbTestForm({
          test_name: '',
          campaign_id: '',
          variant_a_content: '',
          variant_b_content: '',
          test_size: 1000
        });
        loadData();
      } else {
        setMessage('Error creating A/B test');
      }
    } catch (error) {
      setMessage('Error creating A/B test');
      console.error('Error creating A/B test:', error);
    }
  };

  const updateSetting = async (key, value) => {
    try {
      console.log('🔍 Updating marketing setting:', { key, value });
      const response = await whatsappAPI.updateMarketingSettings({ key, value });
      console.log('🔍 Update response:', response);
      
      if (response.data.success) {
        setMessage('Setting updated successfully');
        console.log('🔍 Reloading settings...');
        loadData();
        
        // Clear success message after 3 seconds
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Error updating setting');
        console.error('Failed to update setting:', response.data);
        
        // Clear error message after 5 seconds
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      setMessage('Error updating setting');
      console.error('Error updating setting:', error);
    }
  };

  const addNewSetting = async (e) => {
    e.preventDefault();
    try {
      let finalValue = newSetting.value;
      
      // Convert boolean strings to proper boolean
      if (newSetting.type === 'boolean') {
        finalValue = newSetting.value === 'true' ? 'true' : 'false';
      }
      
      const response = await whatsappAPI.updateMarketingSettings({ 
        key: newSetting.key, 
        value: finalValue 
      });
      
      if (response.data.success) {
        setMessage('New setting added successfully');
        setNewSetting({ key: '', value: '', type: 'boolean' });
        setShowAddSetting(false);
        loadData();
        
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Error adding setting');
      }
    } catch (error) {
      setMessage('Error adding setting');
      console.error('Error adding setting:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      case 'scheduled': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'running': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'completed': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'paused': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'cancelled': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getCampaignTypeIcon = (type) => {
    switch (type) {
      case 'welcome': return <Star size={16} />;
      case 'winback': return <Heart size={16} />;
      case 'seasonal': return <Gift size={16} />;
      case 'promotional': return <TrendingUp size={16} />;
      case 'birthday': return <Gift size={16} />;
      default: return <Send size={16} />;
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'segments', label: 'Customer Segments', icon: Users },
    { id: 'campaigns', label: 'Campaigns', icon: Send },
    { id: 'ab-tests', label: 'A/B Testing', icon: Target },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  const segmentTypes = [
    { value: 'vip', label: 'VIP Customers' },
    { value: 'new', label: 'New Customers' },
    { value: 'inactive', label: 'Inactive Customers' },
    { value: 'seasonal', label: 'Seasonal Customers' },
    { value: 'custom', label: 'Custom Segment' }
  ];

  const campaignTypes = [
    { value: 'welcome', label: 'Welcome Campaign' },
    { value: 'winback', label: 'Winback Campaign' },
    { value: 'seasonal', label: 'Seasonal Campaign' },
    { value: 'promotional', label: 'Promotional Campaign' },
    { value: 'birthday', label: 'Birthday Campaign' },
    { value: 'custom', label: 'Custom Campaign' }
  ];

  return (
    <div className="p-4 sm:p-6 bg-white dark:bg-gray-900 rounded-lg shadow-lg">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          WhatsApp Marketing Automation
        </h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
          Manage customer segments, campaigns, A/B testing, and marketing analytics
        </p>
      </div>

      {message && (
        <div className={`mb-4 p-4 rounded-lg ${
          message.includes('Error') 
            ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' 
            : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
        }`}>
          {message}
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex overflow-x-auto scrollbar-hide">
          <div className="flex space-x-2 sm:space-x-8 min-w-max">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-1 sm:space-x-2 py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon size={16} />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {loading ? (
              <div className="animate-pulse">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:p-6">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="bg-gray-200 dark:bg-gray-700 h-32 rounded-lg"></div>
                  ))}
                </div>
              </div>
            ) : dashboard ? (
              <>
                {/* Dashboard Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:p-6">
                  <div className="bg-blue-50 dark:bg-blue-900 p-4 sm:p-6 rounded-lg">
                    <div className="flex items-center">
                      <Send className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Total Campaigns</p>
                        <p className="text-xl sm:text-2xl font-bold text-blue-900 dark:text-blue-100">{dashboard.totalCampaigns}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900 p-4 sm:p-6 rounded-lg">
                    <div className="flex items-center">
                      <Play className="h-8 w-8 text-green-600 dark:text-green-400" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-green-600 dark:text-green-400">Active Campaigns</p>
                        <p className="text-xl sm:text-2xl font-bold text-green-900 dark:text-green-100">{dashboard.activeCampaigns}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900 p-4 sm:p-6 rounded-lg">
                    <div className="flex items-center">
                      <Users className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Total Customers</p>
                        <p className="text-xl sm:text-2xl font-bold text-purple-900 dark:text-purple-100">{dashboard.totalCustomers}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-yellow-50 dark:bg-yellow-900 p-4 sm:p-6 rounded-lg">
                    <div className="flex items-center">
                      <DollarSign className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Total Revenue</p>
                        <p className="text-xl sm:text-2xl font-bold text-yellow-900 dark:text-yellow-100">₹{dashboard.totalRevenue?.toLocaleString() || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-gray-50 dark:bg-gray-800 p-4 sm:p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                      onClick={() => setActiveTab('campaigns')}
                      className="flex items-center justify-center space-x-2 p-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                    >
                      <Plus size={16} />
                      <span>Create Campaign</span>
                    </button>
                    <button
                      onClick={() => setActiveTab('segments')}
                      className="flex items-center justify-center space-x-2 p-4 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                    >
                      <Users size={16} />
                      <span>Create Segment</span>
                    </button>
                    <button
                      onClick={() => setActiveTab('ab-tests')}
                      className="flex items-center justify-center space-x-2 p-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
                    >
                      <Target size={16} />
                      <span>Create A/B Test</span>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center text-gray-500 dark:text-gray-400">
                No dashboard data available
              </div>
            )}
          </div>
        )}

        {activeTab === 'segments' && (
          <div className="space-y-6">
            <div className="bg-gray-50 dark:bg-gray-800 p-4 sm:p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Create Customer Segment</h3>
              <form onSubmit={createSegment} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Segment Name
                    </label>
                    <input
                      type="text"
                      value={segmentForm.segment_name}
                      onChange={(e) => setSegmentForm({...segmentForm, segment_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="VIP Customers"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Segment Type
                    </label>
                    <select
                      value={segmentForm.segment_type}
                      onChange={(e) => setSegmentForm({...segmentForm, segment_type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      {segmentTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={segmentForm.description}
                    onChange={(e) => setSegmentForm({...segmentForm, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    rows="3"
                    placeholder="Describe this customer segment..."
                  />
                </div>
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center space-x-2"
                >
                  <Plus size={16} />
                  <span>Create Segment</span>
                </button>
              </form>
            </div>

            {/* Segments List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Customer Segments</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {segments.map((segment) => (
                      <tr key={segment.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {segment.segment_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {segment.segment_type}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {segment.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            segment.is_active 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          }`}>
                            {segment.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'campaigns' && (
          <div className="space-y-6">
            <div className="bg-gray-50 dark:bg-gray-800 p-4 sm:p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Create Campaign</h3>
              <form onSubmit={createCampaign} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Campaign Name
                    </label>
                    <input
                      type="text"
                      value={campaignForm.campaign_name}
                      onChange={(e) => setCampaignForm({...campaignForm, campaign_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Summer Sale Campaign"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Campaign Type
                    </label>
                    <select
                      value={campaignForm.campaign_type}
                      onChange={(e) => setCampaignForm({...campaignForm, campaign_type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      {campaignTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Message Content
                  </label>
                  <textarea
                    value={campaignForm.message_content}
                    onChange={(e) => setCampaignForm({...campaignForm, message_content: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    rows="4"
                    placeholder="Enter your campaign message..."
                    required
                  />
                </div>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={campaignForm.is_scheduled}
                      onChange={(e) => setCampaignForm({...campaignForm, is_scheduled: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Schedule Campaign</span>
                  </label>
                  {campaignForm.is_scheduled && (
                    <input
                      type="datetime-local"
                      value={campaignForm.scheduled_at}
                      onChange={(e) => setCampaignForm({...campaignForm, scheduled_at: e.target.value})}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  )}
                </div>
                <button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center space-x-2"
                >
                  <Plus size={16} />
                  <span>Create Campaign</span>
                </button>
              </form>
            </div>

            {/* Campaigns List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Marketing Campaigns</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Campaign</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Recipients</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {campaigns.map((campaign) => (
                      <tr key={campaign.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{campaign.campaign_name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{campaign.segment_name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            {getCampaignTypeIcon(campaign.campaign_type)}
                            <span className="text-sm text-gray-500 dark:text-gray-400">{campaign.campaign_type}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(campaign.status)}`}>
                            {campaign.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {campaign.sent_count}/{campaign.total_recipients}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {campaign.status === 'draft' && (
                            <button
                              onClick={() => executeCampaign(campaign.id)}
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                            >
                              <Play size={16} />
                            </button>
                          )}
                          <button className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300">
                            <Eye size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'ab-tests' && (
          <div className="space-y-6">
            <div className="bg-gray-50 dark:bg-gray-800 p-4 sm:p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Create A/B Test</h3>
              <form onSubmit={createABTest} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Test Name
                    </label>
                    <input
                      type="text"
                      value={abTestForm.test_name}
                      onChange={(e) => setAbTestForm({...abTestForm, test_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Subject Line Test"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Test Size
                    </label>
                    <input
                      type="number"
                      value={abTestForm.test_size}
                      onChange={(e) => setAbTestForm({...abTestForm, test_size: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="1000"
                      min="100"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Variant A Content
                    </label>
                    <textarea
                      value={abTestForm.variant_a_content}
                      onChange={(e) => setAbTestForm({...abTestForm, variant_a_content: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      rows="3"
                      placeholder="Enter variant A content..."
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Variant B Content
                    </label>
                    <textarea
                      value={abTestForm.variant_b_content}
                      onChange={(e) => setAbTestForm({...abTestForm, variant_b_content: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      rows="3"
                      placeholder="Enter variant B content..."
                      required
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md flex items-center space-x-2"
                >
                  <Target size={16} />
                  <span>Create A/B Test</span>
                </button>
              </form>
            </div>

            {/* A/B Tests List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">A/B Tests</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Test Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Campaign</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Winner</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {abTests.map((test) => (
                      <tr key={test.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {test.test_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {test.campaign_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(test.status)}`}>
                            {test.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {test.winner_variant !== 'none' ? `Variant ${test.winner_variant}` : 'No winner yet'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300">
                            <Eye size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="bg-gray-50 dark:bg-gray-800 p-4 sm:p-6 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Marketing Automation Settings</h3>
                <button
                  onClick={() => setShowAddSetting(true)}
                  className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                >
                  <Plus size={16} />
                  <span>Add Setting</span>
                </button>
              </div>
              
              {/* Campaign Settings Suggestions */}
              <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                  💡 Common Campaign Settings You Can Add:
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-blue-700 dark:text-blue-400">
                  <div>• <strong>max_daily_campaigns</strong> - Limit campaigns per day</div>
                  <div>• <strong>campaign_cooldown_hours</strong> - Hours between campaigns</div>
                  <div>• <strong>min_campaign_interval</strong> - Minimum time between campaigns</div>
                  <div>• <strong>max_campaign_budget</strong> - Maximum budget per campaign</div>
                  <div>• <strong>auto_optimization_enabled</strong> - Enable AI optimization</div>
                  <div>• <strong>customer_feedback_enabled</strong> - Collect feedback</div>
                </div>
              </div>
              
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-500 dark:text-gray-400">Loading marketing settings...</p>
                </div>
              ) : Object.keys(settings).length === 0 ? (
                <div className="text-center py-8">
                  <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400 mb-4">No marketing settings found</p>
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Marketing settings will be automatically created when you first use the marketing features.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(settings).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between p-4 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Current: {value}
                        </p>
                      </div>
                      <div className="flex items-center space-x-4">
                        {typeof value === 'boolean' || value === 'true' || value === 'false' ? (
                          <button
                            onClick={() => updateSetting(key, value === 'true' ? 'false' : 'true')}
                            className={`flex items-center space-x-2 px-3 py-1 rounded-md text-sm font-medium ${
                              value === 'true'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                            }`}
                          >
                            {value === 'true' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                            <span>{value === 'true' ? 'Enabled' : 'Disabled'}</span>
                          </button>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              value={value}
                              onChange={(e) => updateSetting(key, e.target.value)}
                              className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                              min="1"
                            />
                            <button
                              onClick={() => updateSetting(key, value)}
                              className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-md hover:bg-blue-200 dark:hover:bg-blue-800"
                            >
                              Update
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add New Setting Modal */}
        {showAddSetting && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-96 max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Add New Marketing Setting
              </h3>
              
              <form onSubmit={addNewSetting} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Setting Key
                  </label>
                  <input
                    type="text"
                    value={newSetting.key}
                    onChange={(e) => setNewSetting({ ...newSetting, key: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., max_daily_campaigns"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Setting Type
                  </label>
                  <select
                    value={newSetting.type}
                    onChange={(e) => setNewSetting({ ...newSetting, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="boolean">Boolean (true/false)</option>
                    <option value="number">Number</option>
                    <option value="text">Text</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Setting Value
                  </label>
                  {newSetting.type === 'boolean' ? (
                    <select
                      value={newSetting.value}
                      onChange={(e) => setNewSetting({ ...newSetting, value: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="true">True</option>
                      <option value="false">False</option>
                    </select>
                  ) : (
                    <input
                      type={newSetting.type === 'number' ? 'number' : 'text'}
                      value={newSetting.value}
                      onChange={(e) => setNewSetting({ ...newSetting, value: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder={newSetting.type === 'number' ? 'e.g., 100' : 'e.g., default_value'}
                      min={newSetting.type === 'number' ? '1' : undefined}
                      required
                    />
                  )}
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                  >
                    Add Setting
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddSetting(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppMarketing;
